import { Container, interfaces } from 'inversify'
import { ErrorPlatformEvent, interpret } from 'xstate-v4'
import { rootContainer } from '~/container'
import sentryCapture from '~/utility/sentryCapture'
import { ResetPasswordConfirmationRepositoryFetch } from './ResetPasswordConfirmationRepositoryFetch'
import { ResetPasswordRepositoryFetch } from './ResetPasswordRepositoryFetch'
import { ValidateResetPasswordTokenRepositoryFetch } from './ValidateResetPasswordTokenRepositoryFetch'
import {
  ResetPasswordConfirmationMachineInterpreter,
  returnResetPasswordConfirmationMachine,
  type ResetPasswordConfirmationContext,
} from './machines/resetPasswordConfirmationMachine'
import {
  ResetPasswordContext,
  ResetPasswordService,
  returnResetPasswordMachine,
} from './machines/resetPasswordMachine'

const resetPasswordContainer = new Container()
resetPasswordContainer.parent = rootContainer

resetPasswordContainer
  .bind<
    interfaces.Factory<ResetPasswordService>
  >('Factory<ResetPasswordService>')
  .toFactory(
    () => (ctx: ResetPasswordContext) =>
      interpret(
        returnResetPasswordMachine()
          .withContext(ctx)
          .withConfig({
            actions: {
              logError: (_, _event: any) => {
                const event: ErrorPlatformEvent = _event
                event.data.name = `resetPasswordMachine ${event.type} error`
                sentryCapture(event.data)
              },
            },
            services: {
              resetPassword: ({ email, next }) =>
                new ResetPasswordRepositoryFetch().reset(email, next),
            },
          }),
      ),
  )

resetPasswordContainer
  .bind<
    interfaces.Factory<ResetPasswordConfirmationMachineInterpreter>
  >('Factory<ResetPasswordConfirmationMachine>')
  .toFactory(
    () => (ctx?: Partial<ResetPasswordConfirmationContext>) =>
      interpret(
        returnResetPasswordConfirmationMachine()
          .withContext({
            ...returnResetPasswordConfirmationMachine().initialState.context,
            ...ctx,
          })
          .withConfig({
            actions: {
              logError: (_, _event: any) => {
                const event: ErrorPlatformEvent = _event
                event.data.name = `resetPasswordConfirmationMachine ${event.type} error`
                sentryCapture(event.data)
              },
            },
            services: {
              changePassword: ({ password, token, uid }) =>
                new ResetPasswordConfirmationRepositoryFetch().reset({
                  password,
                  token,
                  uid,
                }),
              validateToken: ({ token, uid }) =>
                new ValidateResetPasswordTokenRepositoryFetch().validate(
                  token,
                  uid,
                ),
            },
          }),
      ),
  )

export function getResetPasswordServiceFactory() {
  return resetPasswordContainer.get<interfaces.Factory<ResetPasswordService>>(
    'Factory<ResetPasswordService>',
  )
}

export function getResetPasswordConfirmationMachineFactory() {
  return resetPasswordContainer.get<
    interfaces.Factory<ResetPasswordConfirmationMachineInterpreter>
  >('Factory<ResetPasswordConfirmationMachine>')
}
